/**
 * Route Registry
 *
 * Central route definitions with lazy loading for code splitting.
 * Includes route protection for authenticated routes.
 *
 * Route Structure:
 * - Public routes: Landing page, Auth pages
 * - Protected routes: Dashboard, Heroes, Features
 *
 * Lazy pages are wrapped in a stable component so wouter's Route never
 * receives React.lazy() directly (avoids "Cannot convert object to primitive value").
 */

import { lazy, ReactNode, Suspense } from 'react';
import { Redirect, Route, Switch } from 'wouter';
import { useObservabilityClients } from '@tonnie/observability';
import { LoadingSpinner } from '@/components/shell/LoadingSpinner';
import { AppShellLayout } from '@/components/layout/AppShellLayout';
// By path, not the barrel: the eagerly-loaded route table needs only the redirect.
import { LegacyFirstRunRedirect } from '@/features/platform/first-run/components/LegacyFirstRunRedirect';
import { LegacyProfileCompleteRedirect } from '@/features/secondary/profile-basics/components/LegacyProfileCompleteRedirect';
import { profileSectionHref } from '@/features/secondary/profile-sections/lib/profile-sections';
import { ProtectedRoute, PublicOnlyRoute } from '@/components/guards/ProtectedRoute';
import { JourneyInstanceGuard } from '@/components/guards/JourneyInstanceGuard';
import { AccessControl } from '@/components/guards/AccessControl';
import { FeatureFlagRoute } from '@/components/guards/FeatureFlagRoute';
import { SignupRoute } from '@/features/platform/auth/components/SignupRoute';
import { PageErrorBoundary } from '@/components/shell/ErrorBoundary';

/**
 * Wraps lazy() in a stable function component so Route never receives React.lazy() directly.
 * Fixes "Cannot convert object to primitive value" (wouter + Lazy interaction).
 *
 * Each lazy route is wrapped in a PageErrorBoundary so that a render-time
 * crash in one page does not propagate to the top-level boundary and kill
 * the entire app shell (sidebar, header, navigation all stay alive).
 * Error client from observability context is passed so caught errors are reported to Sentry.
 */
function lazyRoute(importFn: () => Promise<{ default: React.ComponentType }>) {
  const LazyComponent = lazy(importFn);
  return function LazyRouteWrapper() {
    const { errors } = useObservabilityClients();
    return (
      <PageErrorBoundary errorClient={errors}>
        <Suspense fallback={<LoadingSpinner />}>
          <LazyComponent />
        </Suspense>
      </PageErrorBoundary>
    );
  };
}

// ═══════════════════════════════════════════════════════════════════════
// LAZY-LOAD FEATURES FOR CODE SPLITTING
// Organized by: heroes/, secondary/, platform/, internal/
// ═══════════════════════════════════════════════════════════════════════

// Platform Features
const LoginPage = lazyRoute(() => import('@/features/platform/auth/pages/LoginPage'));
const SignupPage = lazyRoute(() => import('@/features/platform/auth/pages/SignupPage'));
const ForgotPasswordPage = lazyRoute(
  () => import('@/features/platform/auth/pages/ForgotPasswordPage')
);
// ResetPasswordPage removed — Clerk's reset flow is handled entirely
// within ForgotPasswordPage using the reset_password_email_code strategy.
const SsoCallbackPage = lazyRoute(() => import('@/features/platform/auth/pages/SsoCallbackPage'));
const SignUpContinuePage = lazyRoute(
  () => import('@/features/platform/auth/pages/SignUpContinuePage')
);
const AcceptInvitePage = lazyRoute(
  () => import('@/features/platform/invitations/pages/AcceptInvitePage')
);
const SponsorshipOfferLandingPage = lazyRoute(
  () => import('@/features/platform/sponsorships/pages/SponsorshipOfferLandingPage')
);
const UnsubscribePage = lazyRoute(
  () => import('@/features/platform/notifications/pages/UnsubscribePage')
);
const DashboardPage = lazyRoute(() => import('@/features/platform/dashboard/pages/DashboardPage'));
const JourneyBuilderPage = lazyRoute(
  () => import('@/features/platform/journey/pages/JourneyBuilderPage')
);
const ManageJourneysPage = lazyRoute(
  () => import('@/features/platform/journey/pages/ManageJourneysPage')
);
const BillingPage = lazyRoute(() => import('@/features/platform/billing/pages/BillingPage'));

// Heroes Features
const CheckUpPage = lazyRoute(() => import('@/features/heroes/checkup/pages/CheckUpPage'));
const TestItCatalogPage = lazyRoute(
  () => import('@/features/heroes/testit/pages/TestItCatalogPage')
);
const CheckUpAssessmentPage = lazyRoute(
  () => import('@/features/heroes/checkup/pages/CheckUpAssessmentPage')
);
const CheckUpResultsPage = lazyRoute(
  () => import('@/features/heroes/checkup/pages/CheckUpResultsPage')
);
const TelePortalPage = lazyRoute(() => import('@/features/heroes/teleportal/pages/TelePortalPage'));
const TelePortalSetupPage = lazyRoute(
  () => import('@/features/heroes/teleportal/pages/TelePortalSetupPage')
);
const QultureUpPage = lazyRoute(() => import('@/features/heroes/qultureup/pages/QultureUpPage'));
const LanguageAcademyPage = lazyRoute(
  () => import('@/features/heroes/qultureup/language-academy/pages/LanguageAcademyPage')
);
const QultureUpAssessmentPage = lazyRoute(
  () => import('@/features/heroes/qultureup/pages/QultureUpAssessmentPage')
);
const CarePage = lazyRoute(() => import('@/features/heroes/care/pages/CarePage'));
const CareAssessmentPage = lazyRoute(
  () => import('@/features/heroes/care/pages/CareAssessmentPage')
);
const PassportPage = lazyRoute(() => import('@/features/heroes/passport/pages/PassportPage'));
const InOutPage = lazyRoute(() => import('@/features/heroes/inout/pages/InOutPage'));
const InOutSharedDossierPage = lazyRoute(
  () => import('@/features/heroes/inout/pages/InOutSharedDossierPage')
);

// Secondary Features
const FamilyProgramPage = lazyRoute(
  () => import('@/features/secondary/family/pages/FamilyProgramPage')
);
const CareerPage = lazyRoute(() => import('@/features/secondary/career/pages/CareerPage'));
const CVBuilderPage = lazyRoute(() => import('@/features/secondary/career/pages/CVBuilderPage'));
const SettingsPage = lazyRoute(() => import('@/features/secondary/settings/pages/SettingsPage'));
// ── F50 region: the citizen inbox ──
const InboxPage = lazyRoute(() => import('@/features/platform/inbox/pages/InboxPage'));
// ── end F50 region ──

// Utility Features
const VaultPage = lazyRoute(() => import('@/features/secondary/vault/pages/VaultPage'));
const NotFoundPage = lazyRoute(() => import('@/core/pages/NotFoundPage'));
const DevHealthPage = lazyRoute(() => import('@/features/internal/dev-health/pages/DevHealthPage'));
const DeepLinkLandingPage = lazyRoute(
  () => import('@/features/platform/deep-link/pages/DeepLinkLandingPage')
);

/**
 * Wrapper for dashboard routes that need the layout and authentication
 */
function AuthenticatedDashboardRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <AppShellLayout>{children}</AppShellLayout>
    </ProtectedRoute>
  );
}

/**
 * Wrapper for routes that require a journey instance.
 *
 * Instead of silently redirecting when no journey is active, this renders
 * the AppShellLayout (header, sidebar, journey selector) and shows an
 * in-page guard message prompting the user to select or build a journey.
 *
 * This is the standard pattern for all hero routes (/checkup, /teleportal,
 * /qultureup, /care, /passport). Replicate for any new route that requires
 * a journey context.
 */
function JourneyRequiredRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <AppShellLayout>
        <JourneyInstanceGuard>{children}</JourneyInstanceGuard>
      </AppShellLayout>
    </ProtectedRoute>
  );
}

/** InOut's page behind its flag, signed in, in the app shell. */
function InOutRoute() {
  return (
    <FeatureFlagRoute flag="INOUT_ENABLED">
      <AuthenticatedDashboardRoute>
        <InOutPage />
      </AuthenticatedDashboardRoute>
    </FeatureFlagRoute>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Switch>
        {/* Public Routes - No Auth Required */}
        <Route path="/login">
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        </Route>
        <Route path="/signup">
          <SignupRoute>
            <SignupPage />
          </SignupRoute>
        </Route>
        {/* A social sign-up Clerk could not finish alone (SIGN_UP_CONTINUE_PATH).
            Not public-only: it ends signed in, on its way out. */}
        <Route path="/signup/continue">
          <SignUpContinuePage />
        </Route>
        <Route path="/forgot-password">
          <ForgotPasswordPage />
        </Route>
        {/* Accept-invite landing — public preview, then authed accept.
            Auth state handled inside the page (CTA flips between
            "Sign in to accept" / "Sign up to accept" / "Accept"). */}
        <Route path="/accept-invite">
          <AcceptInvitePage />
        </Route>
        {/* Sponsorship offer landing — citizen receives an email with
            this URL when a B2B admin pays to sponsor their journey.
            Authenticates if needed, then bounces to /dashboard with
            a `sponsorshipToken` query param so the dashboard's
            auto-detect path opens the offer dialog. */}
        <Route path="/sponsorship/offer">
          <SponsorshipOfferLandingPage />
        </Route>
        {/* Email opt-out landing — reached from the footer link in outbound
            mail. Public: the recipient has no session and may have no account,
            so the `?token=` uuid is the credential. The page PREVIEWS and asks
            for confirmation rather than acting on load, because mail scanners
            and link previewers fetch GET links unattended and must not be able
            to opt anyone out. A mail client's own one-click button never comes
            here — it POSTs straight to the API. */}
        <Route path="/unsubscribe">
          <UnsubscribePage />
        </Route>
        {/* Every social sign-in comes back here (SSO_CALLBACK_PATH, @tonnie/sdk). */}
        <Route path="/auth/sso-callback">
          <SsoCallbackPage />
        </Route>
        {/* Easter egg: full-stack health checkup (no auth – dev tool) */}
        <Route path="/dev/health">
          <DevHealthPage />
        </Route>
        {/* Universal-Link landing page. Public. OS should intercept
            these URLs via /.well-known/apple-app-site-association +
            /.well-known/assetlinks.json and open citizen-mobile; this
            route only fires when the handoff didn't happen (desktop
            user, app not installed, or OS verification incomplete).
            See features/platform/deep-link/pages/DeepLinkLandingPage.tsx */}
        <Route path="/l/:rest*">
          <DeepLinkLandingPage />
        </Route>
        {/* /setup and /onboarding — old first-run addresses, redirects only:
            to a profile section, `?next=`, or Home (LegacyFirstRunRedirect).
            A new citizen lands on Home; there is no setup screen. */}
        <Route path="/setup">
          <ProtectedRoute>
            <LegacyFirstRunRedirect />
          </ProtectedRoute>
        </Route>
        <Route path="/onboarding">
          <ProtectedRoute>
            <LegacyFirstRunRedirect />
          </ProtectedRoute>
        </Route>

        {/* Journey Builder - Auth Required, No Layout */}
        <Route path="/journey/builder">
          <ProtectedRoute>
            <JourneyBuilderPage />
          </ProtectedRoute>
        </Route>

        {/* Root redirect: authenticated → dashboard, unauthenticated → login */}
        <Route path="/">
          <PublicOnlyRoute redirectTo="/dashboard">
            <LoginPage />
          </PublicOnlyRoute>
        </Route>

        {/*
          Minhas jornadas — archive, restore, leave, and "who sees my data".
          (W8.1 · F47 · W8.D)

          Inside the dashboard layout, so the switcher stays on screen while
          the citizen manages what it lists.
        */}
        <Route path="/journeys/manage">
          <AuthenticatedDashboardRoute>
            <ManageJourneysPage />
          </AuthenticatedDashboardRoute>
        </Route>

        {/* Dashboard Routes - Auth Required, With Layout */}
        <Route path="/dashboard">
          <AuthenticatedDashboardRoute>
            <DashboardPage />
          </AuthenticatedDashboardRoute>
        </Route>

        {/* TestIt catalog — cross-journey (the citizen's reusable test results) */}
        <Route path="/testit">
          <AuthenticatedDashboardRoute>
            <TestItCatalogPage />
          </AuthenticatedDashboardRoute>
        </Route>
        {/* Heroes - Auth Required, Journey Instance Required */}
        <Route path="/checkup">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="checkup">
              <CheckUpPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        <Route path="/checkup/assessment">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="checkup">
              <CheckUpAssessmentPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        <Route path="/checkup/results">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="checkup">
              <CheckUpResultsPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        <Route path="/teleportal">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="teleportal">
              <TelePortalPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        <Route path="/teleportal/setup">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="teleportal">
              <TelePortalSetupPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        <Route path="/qultureup">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="qultureup">
              <QultureUpPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        {/* Tonnie Languages Academy — QultureUp's third World (Comuniverse), behind
            LANGUAGE_ACADEMY_ENABLED. A journey's: it opens after that journey's
            QultureUp assessment (the page returns to QultureUp before it). No plan
            gates it — open on every tier (nothing is sold here). */}
        <Route path="/qultureup/academy">
          <FeatureFlagRoute flag="LANGUAGE_ACADEMY_ENABLED">
            <JourneyRequiredRoute>
              <LanguageAcademyPage />
            </JourneyRequiredRoute>
          </FeatureFlagRoute>
        </Route>
        <Route path="/qultureup/assessment">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="qultureup">
              <QultureUpAssessmentPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        <Route path="/care/assessment">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="care">
              <CareAssessmentPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        <Route path="/care">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="care">
              <CarePage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>
        <Route path="/passport">
          <JourneyRequiredRoute>
            <AccessControl requireFeature="passport">
              <PassportPage />
            </AccessControl>
          </JourneyRequiredRoute>
        </Route>

        {/* InOut — person-level like TestIt (no journey scopes it), behind
            INOUT_ENABLED. The public calculator is a partner-embed widget
            (calc.tonnie.ai, apps/partner-embed): keeping the answer there hands it to sign-up
            here (?handoff=), and InOut adopts it. */}
        {/* The agency's dossier — where the link in its email lands. Public:
            the token is the credential; nothing is fetched until the agency
            presses download (mail scanners open links, openings are recorded). */}
        <Route path="/dossier/:token">
          <FeatureFlagRoute flag="INOUT_ENABLED">
            <InOutSharedDossierPage />
          </FeatureFlagRoute>
        </Route>
        {/* One page, seven tabs — each its own address (a notice opens one);
            the history's InOuts each their own too. Unkeyed, so moving
            between tabs keeps the page mounted. */}
        <Route path="/inout">
          <InOutRoute />
        </Route>
        <Route path="/inout/limits">
          <InOutRoute />
        </Route>
        <Route path="/inout/trips">
          <InOutRoute />
        </Route>
        <Route path="/inout/documents">
          <InOutRoute />
        </Route>
        <Route path="/inout/notices">
          <InOutRoute />
        </Route>
        <Route path="/inout/agency">
          <InOutRoute />
        </Route>
        <Route path="/inout/history">
          <InOutRoute />
        </Route>
        <Route path="/inout/history/:id">
          <InOutRoute />
        </Route>

        {/* Other Features - Auth Required */}
        <Route path="/family">
          <FeatureFlagRoute flag="FAMILY_ADDON">
            <AuthenticatedDashboardRoute>
              <FamilyProgramPage />
            </AuthenticatedDashboardRoute>
          </FeatureFlagRoute>
        </Route>
        <Route path="/career">
          <FeatureFlagRoute flag="CAREER">
            <AuthenticatedDashboardRoute>
              <CareerPage />
            </AuthenticatedDashboardRoute>
          </FeatureFlagRoute>
        </Route>
        <Route path="/cv-builder">
          <FeatureFlagRoute flag="CAREER">
            <AuthenticatedDashboardRoute>
              <CVBuilderPage />
            </AuthenticatedDashboardRoute>
          </FeatureFlagRoute>
        </Route>
        <Route path="/vault">
          <AuthenticatedDashboardRoute>
            <VaultPage />
          </AuthenticatedDashboardRoute>
        </Route>
        <Route path="/billing">
          <AuthenticatedDashboardRoute>
            <BillingPage />
          </AuthenticatedDashboardRoute>
        </Route>
        <Route path="/settings">
          <AuthenticatedDashboardRoute>
            <SettingsPage />
          </AuthenticatedDashboardRoute>
        </Route>
        {/* ── F50 region ── */}
        <Route path="/inbox">
          <AuthenticatedDashboardRoute>
            <InboxPage />
          </AuthenticatedDashboardRoute>
        </Route>
        {/* ── end F50 region ── */}

        {/* `/profile` — the address people type for their profile: Settings ›
            Profile. It was a "Page not found". */}
        <Route path="/profile">
          <ProtectedRoute>
            <Redirect to={profileSectionHref()} replace />
          </ProtectedRoute>
        </Route>

        {/* The old focused profile page — now Your basics, a window over the
            page `?return=` names (LegacyProfileCompleteRedirect). */}
        <Route path="/profile/complete">
          <ProtectedRoute>
            <LegacyProfileCompleteRedirect />
          </ProtectedRoute>
        </Route>

        {/* 404 - Not Found Page (explicit path so wouter never sees undefined path) */}
        <Route path="*">
          <NotFoundPage />
        </Route>
      </Switch>
    </Suspense>
  );
}
